// 3rd
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import TrioAssets from 'trio-assets-js';

// Styling.
import './scss/app.scss';

// Vue components.
import Accordion from './components/Accordion';
import Blog from './components/Blog';
import HubspotForm from './components/HubspotForm';
import LoadingOverlay from './components/LoadingOverlay';
import MainMenu from './components/MainMenu';
import Parallax from './components/Parallax';
import Projects from './components/Projects';
import Slider from './components/Slider';
import VideoYoutube from './components/VideoYoutube';

// Utils.
import initObserver from './js/utils/reveal';
import { initAnchorLinks } from './js/utils/scrollHelper';

// Elements.
import anchorLink from './js/elements/anchorLink';
import frontPage from './js/elements/frontpage';
import initCollage from './js/elements/collageInteractive';
import initTabs from './js/elements/tabs';
import languageSwitcherInit from './js/elements/languageSwitcher';
import revealLogoWall from './js/elements/logoWall';

(() => {
  initAnchorLinks();
  TrioAssets.parallax();

  // Add anchor link button to the content header (only on CT Agency detail).
  anchorLink();

  // PT Collage interactive.
  const collages = document.querySelectorAll('.collage-interactive');
  collages.forEach((c) => initCollage(c));

  // Logo wall section transition delay for logos.
  revealLogoWall();

  // Init tabs if paragraph is added.
  const tabs = document.querySelectorAll('.paragraph-tabs .tabs');
  if (tabs.length) {
    tabs.forEach((tab) => initTabs(tab));
  }

  // If on front page.
  const isFrontPage = document.querySelector('.node--type-front-page');
  if (isFrontPage) {
    frontPage();
    const introVideo = document.querySelector('#fp-video-intro');
    const loopVideo = document.querySelector('#fp-video-loop');

    if (introVideo && loopVideo) {
      introVideo.addEventListener('ended', () => {
        introVideo.remove();
        loopVideo.classList.remove('front-page-sections__video--hidden');
      });
    }
  }

  // Vue app instances.
  // Loading overlay (fades out after the page is loaded).
  if (document.querySelector('#loading-overlay')) {
    const overlayApp = createApp({
      data() {
        return {};
      },
    });
    overlayApp
      .component('LoadingOverlay', LoadingOverlay)
      .mount('#loading-overlay');
  }

  // Header & main navigation.
  if (document.querySelector('#header')) {
    const headerApp = createApp({
      data() {
        return {
          scrolledHeight: 50,
        };
      },
      mounted() {
        // For contact link.
        initAnchorLinks();

        // Logo animation on scroll.
        const body = document.querySelector('body');
        const siteLogo = document.querySelector('.site-logo');

        if (siteLogo) {
          window.addEventListener('scroll', () => {
            this.handleLogoOnScroll(body, siteLogo);
          });
        }

        // Language switcher.
        const languageSwitcher = document.querySelector('.language-switcher');
        const languagesList = document.querySelector(
          '.language-switcher__items',
        );
        if (languageSwitcher && languagesList) {
          // set initial height
          [languageSwitcher, languagesList].forEach((el) => {
            el.style.height = `${
              languagesList.querySelector('a.is-active')?.clientHeight
            }px`;
          });

          languageSwitcherInit(languageSwitcher, languagesList);
        }
      },
      methods: {
        handleLogoOnScroll(container, logo) {
          if (window.scrollY > this.scrolledHeight) {
            logo.classList.add('site-logo--scroll');
            container.classList.add('body--scrolling');
          } else {
            logo.classList.remove('site-logo--scroll');
            container.classList.remove('body--scrolling');
          }
        },
      },
    });
    headerApp.component('MainMenu', MainMenu).mount('#header');
  }

  // Contact form in footer.
  if (document.querySelector('#footer')) {
    const footerApp = createApp({
      data() {
        return {};
      },
    });
    footerApp.component('HubspotForm', HubspotForm).mount('#footer');
  }

  // Brainfood (blog).
  if (document.querySelector('#blog')) {
    const blogApp = createApp({
      data() {
        return {};
      },
    });
    blogApp.component('Blog', Blog).mount('#blog');
  }

  // Projects page.
  if (document.querySelector('#projects')) {
    const projectsApp = createApp({
      data() {
        return {};
      },
    });
    projectsApp.component('Projects', Projects).mount('#projects');
  }

  // Title area on Agency page.
  if (document.querySelector('#agency-parallax')) {
    const agencyParallaxApp = createApp({
      data() {
        return {};
      },
    });
    agencyParallaxApp
      .component('Parallax', Parallax)
      .component('VideoYoutube', VideoYoutube)
      .use(createPinia())
      .mount('#agency-parallax');
  }

  // Slider paragraphs (Office & Culture).
  const sliderParagraphs = document.querySelectorAll('.slider');
  if (sliderParagraphs.length) {
    sliderParagraphs.forEach((paragraph) => {
      createApp({
        data() {
          return {};
        },
        mounted() {
          initObserver();
        },
      })
        .component('Slider', Slider)
        .mount(paragraph);
    });
  }

  // YouTube videos.
  const youtubeVideos = document.querySelectorAll('.paragraph-video');
  if (youtubeVideos.length) {
    youtubeVideos.forEach((video) => {
      createApp({
        data() {
          return {};
        },
      })
        .component('VideoYoutube', VideoYoutube)
        .use(createPinia())
        .mount(video);
    });
  }

  // Accordion paragraphs.
  // Fixme: IMO accordions don't need to be Vue components.
  const accordionParagraphs = document.querySelectorAll('.paragraph-accordion');
  if (accordionParagraphs.length) {
    accordionParagraphs.forEach((paragraph) => {
      createApp({
        data() {
          return {};
        },
        mounted() {
          initObserver();
        },
      })
        .component('Accordion', Accordion)
        .mount(paragraph);
    });
  }
})();
